import { useState, useEffect, useCallback, useMemo } from 'react';
import { EventTimeline } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { POWER_LEVELS } from '../constants/roomSettings';
import logger from '../utils/logger';

/**
 * Hook for managing individual room member actions and permissions
 * Handles power level changes, kicking, banning, ignoring, and reporting
 *
 * @param {string} roomId - The room ID
 * @param {string} userId - The user ID of the member
 * @returns {Object} Member state and actions
 */
export const useRoomMember = (roomId, userId) => {
  const client = useMatrixClient();
  const [room, setRoom] = useState(null);
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [powerLevel, setPowerLevel] = useState(POWER_LEVELS.DEFAULT);
  const [myPowerLevel, setMyPowerLevel] = useState(POWER_LEVELS.DEFAULT);
  const [powerLevelsContent, setPowerLevelsContent] = useState(null);

  // Load room and member
  useEffect(() => {
    if (!client || !roomId || !userId) {
      setRoom(null);
      setMember(null);
      setIsLoading(false);
      return;
    }

    const roomObject = client.getRoom(roomId);
    if (!roomObject) {
      setRoom(null);
      setMember(null);
      setIsLoading(false);
      return;
    }

    setRoom(roomObject);

    // Get member from room
    const memberObject = roomObject.getMember(userId);
    setMember(memberObject);

    setIsLoading(false);
  }, [client, roomId, userId]);

  // Load power levels and listen for changes
  useEffect(() => {
    if (!room || !client) {
      setPowerLevel(POWER_LEVELS.DEFAULT);
      setMyPowerLevel(POWER_LEVELS.DEFAULT);
      setPowerLevelsContent(null);
      return;
    }

    const loadPowerLevels = () => {
      try {
        const powerLevelsEvent = room
          .getLiveTimeline()
          .getState(EventTimeline.FORWARDS)
          .getStateEvents('m.room.power_levels', '');

        if (!powerLevelsEvent) {
          setPowerLevel(POWER_LEVELS.DEFAULT);
          setMyPowerLevel(POWER_LEVELS.DEFAULT);
          setPowerLevelsContent(null);
          return;
        }

        const content = powerLevelsEvent.getContent();
        setPowerLevelsContent(content);

        // Get target user's power level
        const userLevel = content.users?.[userId] ?? content.users_default ?? POWER_LEVELS.DEFAULT;
        setPowerLevel(userLevel);

        // Get current user's power level
        const myUserId = client.getUserId();
        const myLevel = content.users?.[myUserId] ?? content.users_default ?? POWER_LEVELS.DEFAULT;
        setMyPowerLevel(myLevel);
      } catch (error) {
        logger.error('Error loading power levels:', error);
        setPowerLevel(POWER_LEVELS.DEFAULT);
        setMyPowerLevel(POWER_LEVELS.DEFAULT);
        setPowerLevelsContent(null);
      }
    };

    loadPowerLevels();

    // Listen for power level changes
    const handleStateEvents = (event) => {
      if (event.getType() === 'm.room.power_levels') {
        loadPowerLevels();
      }
    };

    room.on('RoomState.events', handleStateEvents);

    return () => {
      room.off('RoomState.events', handleStateEvents);
    };
  }, [room, client, userId]);

  // Check permissions
  const canModerate = useMemo(() => {
    if (!powerLevelsContent || !client) return false;

    const myUserId = client.getUserId();
    
    // Can't moderate yourself
    if (myUserId === userId) return false;

    // Must have higher power level than target user
    return myPowerLevel > powerLevel;
  }, [powerLevelsContent, client, userId, myPowerLevel, powerLevel]);

  const canKick = useMemo(() => {
    if (!canModerate || !powerLevelsContent) return false;
    const kickLevel = powerLevelsContent.kick ?? POWER_LEVELS.MODERATOR;
    return myPowerLevel >= kickLevel;
  }, [canModerate, powerLevelsContent, myPowerLevel]);

  const canBan = useMemo(() => {
    if (!canModerate || !powerLevelsContent) return false;
    const banLevel = powerLevelsContent.ban ?? POWER_LEVELS.MODERATOR;
    return myPowerLevel >= banLevel;
  }, [canModerate, powerLevelsContent, myPowerLevel]);

  const canSetPowerLevel = useMemo(() => {
    if (!powerLevelsContent || !client) return false;
    
    const myUserId = client.getUserId();
    const isSelf = myUserId === userId;
    
    // Must be able to send m.room.power_levels events
    const powerLevelEventLevel = powerLevelsContent.events?.['m.room.power_levels'] 
      ?? powerLevelsContent.state_default 
      ?? POWER_LEVELS.MODERATOR;
    
    const hasPermissionToChangePowerLevels = myPowerLevel >= powerLevelEventLevel;
    
    if (!hasPermissionToChangePowerLevels) return false;
    
    // Admins can change their own power level
    if (isSelf) {
      return myPowerLevel >= POWER_LEVELS.ADMIN;
    }
    
    // For others, must have canModerate (higher power level than target)
    return canModerate;
  }, [canModerate, powerLevelsContent, myPowerLevel, client, userId]);

  // Set user's power level
  const setUserPowerLevel = useCallback(async (newLevel) => {
    if (!client || !roomId || !userId || !canSetPowerLevel) {
      throw new Error('Cannot set power level: insufficient permissions');
    }

    if (typeof newLevel !== 'number' || newLevel < 0) {
      throw new Error('Power level must be a non-negative number');
    }

    try {
      await client.setPowerLevel(roomId, userId, newLevel);
      logger.info('Set power level for user:', { userId, newLevel });
    } catch (error) {
      logger.error('Error setting power level:', error);
      throw error;
    }
  }, [client, roomId, userId, canSetPowerLevel]);

  // Kick user from room
  const kickUser = useCallback(async (reason = '') => {
    if (!client || !roomId || !userId || !canKick) {
      throw new Error('Cannot kick user: insufficient permissions');
    }

    try {
      await client.kick(roomId, userId, reason);
      logger.info('Kicked user from room:', { userId, reason });
    } catch (error) {
      logger.error('Error kicking user:', error);
      throw error;
    }
  }, [client, roomId, userId, canKick]);

  // Ban user from room
  const banUser = useCallback(async (reason = '') => {
    if (!client || !roomId || !userId || !canBan) {
      throw new Error('Cannot ban user: insufficient permissions');
    }

    try {
      await client.ban(roomId, userId, reason);
      logger.info('Banned user from room:', { userId, reason });
    } catch (error) {
      logger.error('Error banning user:', error);
      throw error;
    }
  }, [client, roomId, userId, canBan]);

  // Unban user from room
  const unbanUser = useCallback(async () => {
    if (!client || !roomId || !userId || !canBan) {
      throw new Error('Cannot unban user: insufficient permissions');
    }

    try {
      await client.unban(roomId, userId);
      logger.info('Unbanned user from room:', { userId });
    } catch (error) {
      logger.error('Error unbanning user:', error);
      throw error;
    }
  }, [client, roomId, userId, canBan]);

  // Check if user is banned from the room
  const isBanned = useMemo(() => {
    if (!room || !userId) return false;

    try {
      const memberEvent = room.currentState.getStateEvents('m.room.member', userId);
      if (!memberEvent) return false;

      const membership = memberEvent.getContent()?.membership;
      return membership === 'ban';
    } catch (error) {
      logger.error('Error checking ban status:', error);
      return false;
    }
  }, [room, userId]);

  // Ignore user (account-level, not room-specific)
  const ignoreUser = useCallback(async () => {
    if (!client || !userId) {
      throw new Error('Cannot ignore user: missing client or userId');
    }

    const myUserId = client.getUserId();
    if (myUserId === userId) {
      throw new Error('Cannot ignore yourself');
    }

    try {
      // Get current ignore list
      const ignoredUsers = client.getIgnoredUsers();
      
      // Check if already ignored
      if (ignoredUsers.includes(userId)) {
        logger.info('User is already ignored:', userId);
        return;
      }

      // Add to ignore list
      await client.setIgnoredUsers([...ignoredUsers, userId]);
      logger.info('Ignored user:', userId);
    } catch (error) {
      logger.error('Error ignoring user:', error);
      throw error;
    }
  }, [client, userId]);

  const unignoreUser = useCallback(async () => {
    if (!client || !userId) {
      throw new Error('Cannot unignore user: missing client or userId');
    }

    try {
      // Get current ignore list
      const ignoredUsers = client.getIgnoredUsers();
      
      // Check if user is in ignore list
      if (!ignoredUsers.includes(userId)) {
        logger.info('User is not ignored:', userId);
        return;
      }

      // Remove from ignore list
      const newIgnoredUsers = ignoredUsers.filter(id => id !== userId);
      await client.setIgnoredUsers(newIgnoredUsers);
      logger.info('Unignored user:', userId);

      // Reload to pick up conversations created during the ignore period.
      // Synapse filters invites from ignored users during /sync and does NOT
      // retroactively deliver them after unignoring. A fresh page load forces
      // a full initial sync which discovers all current room memberships.
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      logger.error('Error unignoring user:', error);
      throw error;
    }
  }, [client, userId]);

  // Report user (server-level)
  const reportUser = useCallback(async (reason = '', score = -100) => {
    if (!client || !roomId || !userId) {
      throw new Error('Cannot report user: missing required data');
    }

    // Validate score per Matrix spec: -100 (most offensive) to 0 (least offensive)
    if (typeof score !== 'number' || isNaN(score) || score < -100 || score > 0) {
      throw new Error('Score must be a number between -100 and 0');
    }

    const myUserId = client.getUserId();
    if (myUserId === userId) {
      throw new Error('Cannot report yourself');
    }

    try {
      // Report content endpoint expects an event ID, but we can use a dummy one
      // or find the most recent event from this user
      let eventId = null;

      if (room) {
        const timeline = room.getLiveTimeline().getEvents();
        // Find most recent event from this user
        for (let i = timeline.length - 1; i >= 0; i--) {
          const event = timeline[i];
          if (event.getSender() === userId) {
            eventId = event.getId();
            break;
          }
        }
      }

      if (!eventId) {
        throw new Error('No events found from this user to report');
      }

      // Report the event
      await client.reportEvent(roomId, eventId, score, reason);
      logger.info('Reported user:', { userId, eventId, reason, score });
    } catch (error) {
      logger.error('Error reporting user:', error);
      throw error;
    }
  }, [client, roomId, userId, room]);

  return {
    // State
    member,
    room,
    isLoading,
    powerLevel,
    myPowerLevel,
    isBanned,
    
    // Permissions
    canModerate,
    canKick,
    canBan,
    canSetPowerLevel,
    
    // Actions
    setUserPowerLevel,
    kickUser,
    banUser,
    unbanUser,
    ignoreUser,
    unignoreUser,
    reportUser,
  };
};

export default useRoomMember;
